import { useCallback, useEffect, useRef, useState } from "react";
import { chat, transcribe, speak } from "../api/client";
import type { ChatMessage, JarvisState } from "../types";
import { useAudioRecorder } from "./useAudioRecorder";
import { useConversation } from "./useConversation";
import { useMicStream } from "./useMicStream";
import { useSoundEffects } from "./useSoundEffects";
import { useVoiceActivityDetector } from "./useVoiceActivityDetector";
import type { JarvisSettings } from "./useSettings";

const MIME_EXTENSIONS: Record<string, string> = {
  "audio/mp4": "m4a",
  "audio/webm": "webm",
  "audio/ogg": "ogg",
};

function extensionFor(mimeType: string): string {
  const base = mimeType.split(";")[0];
  return MIME_EXTENSIONS[base] ?? "webm";
}

const STATUS_TEXT: Record<JarvisState, string> = {
  idle: "Som pripravený.",
  listening: "Počúvam ťa.",
  thinking: "Premýšľam…",
  speaking: "Hovorím…",
  error: "Nastala chyba.",
};

const HANDS_FREE_IDLE_TEXT = "Hands-free: môžeš hovoriť kedykoľvek.";

// A ~0.1s silent WAV, used only to "unlock" programmatic audio.play() on iOS
// Safari. Safari blocks play() calls that aren't a direct result of a user
// gesture; in hands-free mode the response is played back after a voice
// detection, not a tap, so without this the very first playback throws
// NotAllowedError. Playing (and immediately pausing) this silent clip inside
// a real tap handler marks the shared <audio> element as allowed to keep
// playing programmatically for the rest of the page's lifetime.
const SILENT_WAV_DATA_URI =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQQAAAAAAAAA";

export function useJarvis(settings: JarvisSettings) {
  const [state, setState] = useState<JarvisState>("idle");
  const [statusText, setStatusText] = useState(STATUS_TEXT.idle);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [handsFreeEnabled, setHandsFreeEnabled] = useState(false);

  const recorder = useAudioRecorder();
  const {
    stream: micStreamValue,
    ensure: micStreamEnsure,
    release: micStreamRelease,
    permissionError: micPermissionError,
  } = useMicStream();
  const conversation = useConversation();
  const playTone = useSoundEffects(settings.soundEffectsEnabled);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const requestInFlightRef = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const handsFreeRef = useRef(handsFreeEnabled);
  handsFreeRef.current = handsFreeEnabled;

  // Kept in a ref so goToError has a stable identity without re-creating on
  // every settings change (its useCallback deps stay empty).
  const playToneRef = useRef(playTone);
  playToneRef.current = playTone;

  const getAudioElement = useCallback((): HTMLAudioElement => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    return audioRef.current;
  }, []);

  // Must run synchronously inside a real tap handler (before any `await`) to
  // count as a user gesture for iOS Safari's autoplay policy.
  const unlockAudioPlayback = useCallback(() => {
    const audio = getAudioElement();
    audio.src = SILENT_WAV_DATA_URI;
    void audio
      .play()
      .then(() => audio.pause())
      .catch(() => {});
  }, [getAudioElement]);

  const goToError = useCallback(
    (message: string) => {
      setErrorDetail(message);
      setState("error");
      setStatusText(STATUS_TEXT.error);
      playToneRef.current("error");
      // An error drops back to plain tap-to-talk — hands-free that keeps
      // misfiring after a failure would be more annoying than helpful.
      setHandsFreeEnabled(false);
      micStreamRelease();
      setTimeout(() => {
        setState("idle");
        setStatusText(STATUS_TEXT.idle);
      }, 2500);
    },
    [micStreamRelease],
  );

  const startListening = useCallback(async () => {
    if (requestInFlightRef.current || stateRef.current === "listening") return;
    try {
      const stream = await micStreamEnsure();
      recorder.start(stream);
      setState("listening");
      setStatusText(STATUS_TEXT.listening);
      playToneRef.current("start");
    } catch (error) {
      goToError(error instanceof Error ? error.message : "Mikrofón nie je dostupný.");
    }
  }, [goToError, micStreamEnsure, recorder]);

  const stopAndSend = useCallback(async () => {
    if (stateRef.current !== "listening") return;
    playToneRef.current("stop");
    const result = await recorder.stop();
    if (!result) {
      setState("idle");
      setStatusText(handsFreeRef.current ? HANDS_FREE_IDLE_TEXT : STATUS_TEXT.idle);
      if (!handsFreeRef.current) micStreamRelease();
      return;
    }

    requestInFlightRef.current = true;
    setState("thinking");
    setStatusText(STATUS_TEXT.thinking);

    try {
      const filename = `nahravka.${extensionFor(result.mimeType)}`;
      const transcript = await transcribe(result.blob, filename);
      if (!transcript.trim()) {
        requestInFlightRef.current = false;
        setState("idle");
        setStatusText(handsFreeRef.current ? HANDS_FREE_IDLE_TEXT : STATUS_TEXT.idle);
        if (!handsFreeRef.current) micStreamRelease();
        return;
      }

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: transcript.trim(),
      };
      conversation.addMessage(userMessage);

      const context = conversation.contextForRequest(userMessage);
      const reply = await chat(context);

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: reply,
      };
      conversation.addMessage(assistantMessage);

      setState("speaking");
      setStatusText(STATUS_TEXT.speaking);

      const audioBlob = await speak(reply, settings.voice);
      const url = URL.createObjectURL(audioBlob);
      const audio = getAudioElement();
      audio.playbackRate = settings.playbackRate;

      const finishPlayback = () => {
        URL.revokeObjectURL(url);
        requestInFlightRef.current = false;
        setState("idle");
        setStatusText(handsFreeRef.current ? HANDS_FREE_IDLE_TEXT : STATUS_TEXT.idle);
        if (!handsFreeRef.current) micStreamRelease();
      };

      let retried = false;
      const attemptPlay = async (): Promise<void> => {
        audio.onended = finishPlayback;
        audio.onerror = () => {
          if (!retried) {
            // Transient media/network hiccups happen; one silent retry
            // before bothering the user with an error.
            retried = true;
            attemptPlay().catch(() => {
              URL.revokeObjectURL(url);
              requestInFlightRef.current = false;
              goToError("Prehrávanie odpovede sa nepodarilo.");
            });
            return;
          }
          URL.revokeObjectURL(url);
          requestInFlightRef.current = false;
          goToError("Prehrávanie odpovede sa nepodarilo.");
        };
        audio.src = url;
        await audio.play();
      };

      await attemptPlay();
    } catch (error) {
      requestInFlightRef.current = false;
      goToError(error instanceof Error ? error.message : "Nastala neočakávaná chyba.");
    }
  }, [conversation, getAudioElement, goToError, micStreamRelease, recorder, settings.playbackRate, settings.voice]);

  useVoiceActivityDetector({
    stream: micStreamValue,
    active: handsFreeEnabled && (state === "idle" || state === "listening"),
    onSpeechStart: useCallback(() => {
      if (stateRef.current === "idle") void startListening();
    }, [startListening]),
    onSpeechEnd: useCallback(() => {
      if (stateRef.current === "listening") void stopAndSend();
    }, [stopAndSend]),
  });

  const cancelListening = useCallback(() => {
    if (stateRef.current !== "listening") return;
    recorder.cancel();
    setState("idle");
    setStatusText(handsFreeRef.current ? HANDS_FREE_IDLE_TEXT : STATUS_TEXT.idle);
    if (!handsFreeRef.current) micStreamRelease();
  }, [micStreamRelease, recorder]);

  const stopSpeaking = useCallback(() => {
    if (stateRef.current !== "speaking") return;
    audioRef.current?.pause();
    requestInFlightRef.current = false;
    setState("idle");
    setStatusText(handsFreeRef.current ? HANDS_FREE_IDLE_TEXT : STATUS_TEXT.idle);
    if (!handsFreeRef.current) micStreamRelease();
  }, [micStreamRelease]);

  const toggleListening = useCallback(() => {
    unlockAudioPlayback();
    if (stateRef.current === "idle" || stateRef.current === "error") {
      void startListening();
    } else if (stateRef.current === "listening") {
      void stopAndSend();
    } else if (stateRef.current === "speaking") {
      stopSpeaking();
    }
    // "thinking" ignores taps — request already in flight.
  }, [startListening, stopAndSend, stopSpeaking, unlockAudioPlayback]);

  const toggleHandsFree = useCallback(async () => {
    if (handsFreeRef.current) {
      setHandsFreeEnabled(false);
      if (stateRef.current === "idle") micStreamRelease();
      setStatusText(STATUS_TEXT.idle);
      return;
    }
    unlockAudioPlayback();
    try {
      await micStreamEnsure();
      setHandsFreeEnabled(true);
      if (stateRef.current === "idle") setStatusText(HANDS_FREE_IDLE_TEXT);
    } catch (error) {
      goToError(error instanceof Error ? error.message : "Mikrofón nie je dostupný.");
    }
  }, [goToError, micStreamEnsure, micStreamRelease, unlockAudioPlayback]);

  // Release the mic the moment the user navigates away while hands-free is on.
  useEffect(() => {
    return () => {
      micStreamRelease();
    };
  }, [micStreamRelease]);

  return {
    state,
    statusText,
    errorDetail,
    recordingSeconds: recorder.recordingSeconds,
    micPermissionError,
    handsFreeEnabled,
    toggleHandsFree,
    toggleListening,
    cancelListening,
    history: conversation.history,
    clearHistory: conversation.clearHistory,
  };
}
